/**
 * @file Keeps --header-wrapper-height in sync with the fixed header's real
 * rendered height (body padding-top and the anchor scroll offset both read
 * this custom property), and hides/reveals the header as the user scrolls.
 *
 * Logged-in editors don't get the fixed/sticky header at all.
 */
import { rafThrottle } from "../../../../assets/js/_helpers.js";

/** Modifier class that slides the header out of view. */
const HIDDEN_CLASS = "header-wrapper--hidden";

/**
 * Minimum upward scroll distance, in px, within UP_SCROLL_WINDOW_MS that
 * counts as a deliberate attempt to scroll back to the top — and so reveals
 * the header even mid-page. Tune alongside UP_SCROLL_WINDOW_MS.
 */
const UP_SCROLL_DISTANCE = 200;

/** Time window, in ms, that UP_SCROLL_DISTANCE must occur within. */
const UP_SCROLL_WINDOW_MS = 1000;

/**
 * Observes the header wrapper and writes its measured height to the
 * --header-wrapper-height custom property on the root element.
 *
 * A ResizeObserver catches every cause of a height change — viewport
 * resize, orientation change, font-swap reflow — in one mechanism.
 *
 * @param {HTMLElement} headerWrapperEl
 */
export function headerWrapper(headerWrapperEl) {
  // Logged-in editors get a normal, non-fixed header (see header-wrapper.css)
  // — none of the height-sync/hide-on-scroll behavior below applies to them.
  if (document.body.classList.contains("user-logged-in")) return;

  const setHeight = rafThrottle(() => {
    document.documentElement.style.setProperty(
      "--header-wrapper-height",
      `${headerWrapperEl.offsetHeight}px`,
    );
  });

  new ResizeObserver(setHeight).observe(headerWrapperEl);

  initAutoHide(headerWrapperEl);
}

/**
 * Hides the header while the user scrolls down, and reveals it at the top
 * of the page or on a deliberate upward scroll (see UP_SCROLL_DISTANCE /
 * UP_SCROLL_WINDOW_MS) — the assumption being they're heading back to top.
 *
 * Hiding doesn't engage at all until the user has scrolled down past the
 * header's own height (--header-wrapper-height) — before that, the header
 * hasn't fully cleared the top of the viewport yet, so sliding it away
 * would look premature.
 *
 * @param {HTMLElement} headerWrapperEl
 */
function initAutoHide(headerWrapperEl) {
  let lastScrollY = window.scrollY;

  // Marks the start of the current upward scroll run: the highest scrollY
  // reached before the user started scrolling up, and when that run began.
  let upScrollAnchorY = lastScrollY;
  let upScrollAnchorTime = performance.now();

  const onScroll = rafThrottle(() => {
    const currentScrollY = window.scrollY;
    const now = performance.now();
    const headerHeight =
      parseFloat(
        getComputedStyle(document.documentElement).getPropertyValue(
          "--header-wrapper-height",
        ),
      ) || 0;

    if (currentScrollY <= headerHeight) {
      headerWrapperEl.classList.remove(HIDDEN_CLASS);
      upScrollAnchorY = currentScrollY;
      upScrollAnchorTime = now;
    } else if (currentScrollY > lastScrollY) {
      // Scrolling down — hide, and reset the upward-run anchor.
      headerWrapperEl.classList.add(HIDDEN_CLASS);
      upScrollAnchorY = currentScrollY;
      upScrollAnchorTime = now;
    } else if (currentScrollY < lastScrollY) {
      // Scrolling up — restart the anchor once its window has expired, so
      // a slow drift up never accumulates into a reveal.
      if (now - upScrollAnchorTime > UP_SCROLL_WINDOW_MS) {
        upScrollAnchorY = currentScrollY;
        upScrollAnchorTime = now;
      }

      if (upScrollAnchorY - currentScrollY >= UP_SCROLL_DISTANCE) {
        headerWrapperEl.classList.remove(HIDDEN_CLASS);
      }
    }

    lastScrollY = currentScrollY;
  });

  window.addEventListener("scroll", onScroll, { passive: true });
}
